import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

type Scope = 'GLOBAL' | 'SERVICE';

const CallQaScorecardsPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.scorecards.read');
  const canManage = permissions.includes('call_qa.scorecards.manage');
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('GLOBAL');
  const [serviceId, setServiceId] = useState<string>('');
  const [draft, setDraft] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Service catalog for the Service scope picker.
  const { data: servicesData } = useQuery({
    queryKey: ['callQaServices'],
    queryFn: () => apiClient.callQa.listServices(),
    enabled: canRead,
  });
  const services: any[] = Array.isArray(servicesData?.data?.data) ? servicesData!.data!.data : [];

  const isService = scope === 'SERVICE';
  const serviceReady = isService && !!serviceId;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['callQaScorecard', scope, serviceId],
    queryFn: () => (isService
      ? apiClient.callQa.getServiceScorecard(serviceId)
      : apiClient.callQa.getGlobalScorecard()),
    enabled: canRead && (!isService || serviceReady),
  });

  // For GLOBAL the payload is the card; for SERVICE it is { service, scorecard, scorecard_versions }.
  const card = isService ? (data?.data?.data?.scorecard ?? null) : (data?.data?.data ?? null);
  const serviceCardMissing = isService && serviceReady && !isLoading && data != null && card == null;
  const versions: any[] = isService
    ? (Array.isArray(data?.data?.data?.scorecard_versions) ? data!.data!.data.scorecard_versions : [])
    : [];

  const { data: globalVersionsData } = useQuery({
    queryKey: ['callQaScorecardVersions'],
    queryFn: () => apiClient.callQa.listScorecardVersions(),
    enabled: canRead && !isService,
  });
  const globalVersions: any[] = Array.isArray(globalVersionsData?.data?.data) ? globalVersionsData!.data!.data : [];

  useEffect(() => {
    setDraft(card ? JSON.parse(JSON.stringify(card)) : null);
    setSaved(false);
  }, [card]);

  const buildPayload = () => ({
    name: draft.name,
    notes: draft.notes,
    criteria: (draft.criteria || []).map((c: any) => ({
      section: c.section,
      name: c.name,
      description: c.description,
      evaluation_instructions: c.evaluation_instructions,
      weight: Number(c.weight || 0),
      required: Boolean(c.required),
      critical_failure_capable: Boolean(c.critical_failure_capable),
      enabled: c.enabled !== false,
      sort_order: Number(c.sort_order || 0),
      match_hints: c.match_hints || [],
    })),
  });

  const saveMutation = useMutation({
    mutationFn: () => (isService
      ? apiClient.callQa.updateServiceScorecard(serviceId, buildPayload())
      : apiClient.callQa.updateGlobalScorecard(buildPayload())),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callQaScorecard'] });
      queryClient.invalidateQueries({ queryKey: ['callQaScorecardVersions'] });
      setSaved(true);
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Save failed');
    },
  });

  const createFromGlobalMutation = useMutation({
    mutationFn: () => apiClient.callQa.createServiceScorecard(serviceId),
    onSuccess: () => { setError(null); refetch(); },
    onError: (err: any) => setError(err?.response?.data?.message || 'Create failed'),
  });

  const deleteVersionMutation = useMutation({
    mutationFn: (scorecardId: string) => apiClient.callQa.deleteScorecardVersion(scorecardId),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['callQaScorecard'] });
      queryClient.invalidateQueries({ queryKey: ['callQaScorecardVersions'] });
      refetch();
    },
    onError: (err: any) => setError(err?.response?.data?.message || err?.message || 'Delete failed'),
  });

  const handleDeleteVersion = (v: any) => {
    if (v?.status === 'ACTIVE') return;
    if (window.confirm(`Delete scorecard version "${v.version_label}"? Past calls keep their own snapshot, so their scores are not affected. This cannot be undone.`)) {
      deleteVersionMutation.mutate(v.id);
    }
  };

  const weightPreview = useMemo(() => {
    if (!draft?.criteria) return { positive: 0, enabled: 0 };
    const enabled = (draft.criteria as any[]).filter((c) => c.enabled !== false);
    const positive = enabled
      .filter((c) => Number(c.weight) > 0)
      .reduce((s, c) => s + Number(c.weight || 0), 0);
    return { positive, enabled: enabled.length };
  }, [draft]);

  if (!canRead) {
    return <Alert severity="warning">Scorecards require call_qa.scorecards.read.</Alert>;
  }

  const scopeSelector = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={scope}
        onChange={(_e, v) => { if (v) { setScope(v); setSaved(false); } }}
      >
        <ToggleButton value="GLOBAL">Global</ToggleButton>
        <ToggleButton value="SERVICE">Service</ToggleButton>
      </ToggleButtonGroup>
      {isService && (
        <TextField
          select
          size="small"
          label="Service"
          value={serviceId}
          sx={{ minWidth: 260 }}
          onChange={(e) => { setServiceId(e.target.value); setSaved(false); }}
        >
          {services.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );

  if (isService && !serviceReady) {
    return (
      <Box>
        {scopeSelector}
        <Alert severity="info">Select a service to view or build its scorecard.</Alert>
      </Box>
    );
  }
  if (isLoading) return (<Box>{scopeSelector}<CircularProgress size={24} /></Box>);

  if (serviceCardMissing) {
    return (
      <Box>
        {scopeSelector}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          This service has no scorecard yet, so its campaigns use the Global scorecard. Create one
          from the Global template to start customizing it.
        </Alert>
        {canManage && (
          <Button
            variant="contained"
            disabled={createFromGlobalMutation.isPending}
            onClick={() => createFromGlobalMutation.mutate()}
          >
            Create from Global
          </Button>
        )}
      </Box>
    );
  }

  if (!draft) return (<Box>{scopeSelector}<CircularProgress size={24} /></Box>);

  return (
    <Box>
      {scopeSelector}
      <Typography variant="h6" gutterBottom>
        {isService ? 'Service Scorecard' : 'Global Scorecard'} — {draft.version_label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Editing creates a new version. Historical call scores keep their original scorecard snapshot.
        {isService && ' Campaigns linked to this service use this card unless they have their own.'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 1 }}>New scorecard version saved.</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Name"
            value={draft.name || ''}
            disabled={!canManage}
            onChange={(e) => { setSaved(false); setDraft((d: any) => ({ ...d, name: e.target.value })); }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2">
              Enabled positive weights: <strong>{weightPreview.positive}</strong> / 100
            </Typography>
            {Math.abs(weightPreview.positive - 100) > 0.01 && (
              <Alert severity="warning" sx={{ mt: 1 }}>Weights must total 100 before save.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1180, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Section</TableCell>
              <TableCell sx={{ width: '16%' }}>Criterion</TableCell>
              <TableCell sx={{ width: 120 }}>Weight</TableCell>
              <TableCell sx={{ width: 88 }}>Critical</TableCell>
              <TableCell sx={{ width: 88 }}>Enabled</TableCell>
              <TableCell sx={{ width: 'auto' }}>Instructions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(draft.criteria || []).map((c: any, idx: number) => (
              <TableRow key={c.id || idx}>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={c.section}
                    disabled={!canManage}
                    onChange={(e) => {
                      setSaved(false);
                      setDraft((d: any) => {
                        const criteria = [...d.criteria];
                        criteria[idx] = { ...criteria[idx], section: e.target.value };
                        return { ...d, criteria };
                      });
                    }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={c.name}
                    disabled={!canManage}
                    onChange={(e) => {
                      setSaved(false);
                      setDraft((d: any) => {
                        const criteria = [...d.criteria];
                        criteria[idx] = { ...criteria[idx], name: e.target.value };
                        return { ...d, criteria };
                      });
                    }}
                  />
                </TableCell>
                <TableCell sx={{ width: 120, verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={c.weight}
                    disabled={!canManage}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 1,
                      style: { textAlign: 'right', width: 72 },
                    }}
                    sx={{
                      width: 96,
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                      '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    onChange={(e) => {
                      setSaved(false);
                      setDraft((d: any) => {
                        const criteria = [...d.criteria];
                        criteria[idx] = { ...criteria[idx], weight: Number(e.target.value) };
                        return { ...d, criteria };
                      });
                    }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        size="small"
                        checked={Boolean(c.critical_failure_capable)}
                        disabled={!canManage}
                        onChange={(e) => {
                          setSaved(false);
                          setDraft((d: any) => {
                            const criteria = [...d.criteria];
                            criteria[idx] = { ...criteria[idx], critical_failure_capable: e.target.checked };
                            return { ...d, criteria };
                          });
                        }}
                      />
                    )}
                    label=""
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Switch
                    size="small"
                    checked={c.enabled !== false}
                    disabled={!canManage}
                    onChange={(e) => {
                      setSaved(false);
                      setDraft((d: any) => {
                        const criteria = [...d.criteria];
                        criteria[idx] = { ...criteria[idx], enabled: e.target.checked };
                        return { ...d, criteria };
                      });
                    }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    value={c.evaluation_instructions || ''}
                    disabled={!canManage}
                    onChange={(e) => {
                      setSaved(false);
                      setDraft((d: any) => {
                        const criteria = [...d.criteria];
                        criteria[idx] = { ...criteria[idx], evaluation_instructions: e.target.value };
                        return { ...d, criteria };
                      });
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {canManage && (
        <Button
          variant="contained"
          disabled={saveMutation.isPending || Math.abs(weightPreview.positive - 100) > 0.01}
          onClick={() => saveMutation.mutate()}
        >
          Save as New Version
        </Button>
      )}

      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>Versions</Typography>
        {(isService ? versions : globalVersions).map((v: any) => (
          <Chip
            key={v.id}
            size="small"
            label={`${v.version_label} (${v.status})`}
            sx={{ mr: 1, mb: 1 }}
            color={v.status === 'ACTIVE' ? 'primary' : 'default'}
            onDelete={canManage && v.status !== 'ACTIVE' ? () => handleDeleteVersion(v) : undefined}
            disabled={deleteVersionMutation.isPending}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CallQaScorecardsPage;
